import { Context, PageDsl, Params, ModalDsl, GenericWarning, ColumnSection, HTTPMethod } from '../types';
import React, { useReducer, createContext, useContext, useRef, useEffect, useState, useMemo } from 'react';
import { pageClient } from '../client';
import { useIsMounted } from '../tools';
import { initialState, reducer } from './reducer';
import { ContextManager } from '../context/manager';
import { PageSettingsManager } from './settings';
import { makeUrl } from '../tools/strings';
import { DEFAULT_ERROR_MESSAGE } from '../common/constants/error';
import { useSavedView } from '../tools/useSavedView';
import { CorePageProviderProps, PageSettings, UpsellContext } from './types';
import { ContentElementHasData } from '../content/types';
import { FilterTrayTabId } from '@ecosystem/newron-design-system/build/exports/containers';
import {
  checkIfCurrencyChanged,
  getCollectionsContext,
  getIsLiveValueForProvider,
  handleInsightDataChange,
} from './utils';
import { TrayType } from '../types/content/custom.types';
import { useMobile } from '../context/mobile/mobile.provider';
import { getPageDsl } from '../client/queryClient/pageDsl';
import { getPageView, NavRoutesRegister } from '@ecosystem/newron-client-lib';
import { BULK_EXPORT_URL } from '../data';

export type ComponentHasData = ContentElementHasData;

export type PageContextType = Omit<CorePageProviderProps, 'children'> & {
  url?: string;
  dsl?: PageDsl;
  error?: string;
  pageStamp: number;
  componentHasDataMap: Record<string, ComponentHasData>;
  setComponentHasData: (componentName: string, value: ComponentHasData) => void;
  allComponentsHaveNoData: boolean;
  someComponentsHaveData: boolean;
  endpoints?: {
    page: string;
    data: string;
  };
  contextParams?: Params;
  contextLoading?: boolean;
  pageParams?: Params;
  dslLoading?: boolean;
  dataLoading?: boolean;
  modalDsl?: ModalDsl;
  globalWarnings: GenericWarning[];
  pageWarnings: GenericWarning[];
  updatePageSettings: (updates: Params) => void;
  openModal: (name: string, endpoint: string, params?: Params) => void;
  closeModal: () => void;
  setGlobalWarnings: (globalWarnings: GenericWarning[], resetWarnings?: boolean) => void;
  resetGlobalWarnings: () => void;
  setPageWarnings: (pageWarnings: GenericWarning[]) => void;
  resetPageWarnings: () => void;
  setDataLoading: (isDataLoading: boolean, requestId: string) => void;
  trayType?: TrayType;
  setCustomizationTray: (trayType: TrayType) => void;
  customizationTrayTabId?: FilterTrayTabId;
  setCustomizationTrayTabId?: (tabId: FilterTrayTabId) => void;
  isCollections?: boolean;
  isFilterTray?: boolean;
  setIsFilterTray?: (isFilterTray: boolean) => void;
  isDslLoadedInCollection?: boolean;
  setIsDslLoadedForCollection?: (status: boolean) => void;
  upsellContext: UpsellContext;
};

const stateContext = createContext<PageContextType>({
  pageStamp: -1,
  componentHasDataMap: {},
  globalWarnings: [],
  pageWarnings: [],
  updatePageSettings: () => {},
  openModal: () => {},
  closeModal: () => {},
  setGlobalWarnings: (_: GenericWarning[]) => {},
  setPageWarnings: (_: GenericWarning[]) => {},
  setDataLoading: (_: boolean) => {},
  resetGlobalWarnings: () => {},
  resetPageWarnings: () => {},
  setComponentHasData: () => {},
  allComponentsHaveNoData: true,
  someComponentsHaveData: false,
  collection: { url: '' },
  context: { name: '', url: '' },
  data: { url: '', requestMethod: HTTPMethod.GET },
  page: { name: '', url: '' },
  setCustomizationTray: () => {},
  setCustomizationTrayTabId: () => {},
  setIsFilterTray: () => {},
  isDslLoadedInCollection: false,
  upsellContext: { upsellAnalyticsURL: '', upsellQualtricsURL: '' },
});

function CorePageProvider({
  children,
  context,
  page,
  data,
  collection,
  upsellContext,
  isBulkExport,
}: Readonly<CorePageProviderProps>) {
  const [state, dispatch] = useReducer(reducer, initialState());
  const [insightPageSettings, setInsightPageSettings] = useState<PageSettings>();
  const [componentHasDataMap, setComponentHasDataMap] = useState<Record<string, ComponentHasData>>({});
  const pagePending = useRef<boolean>(false);
  const isMounted = useIsMounted();
  const pageNameRef = useRef<string>(null);
  const dataLoadingState = useRef<{ [x: string]: boolean }>({});
  const { isMobile } = useMobile();

  const dispatchCallback = () => {
    dispatch({
      type: 'EXPIRE_PAGE',
      payload: undefined,
    });
  };

  const collectionsContext =
    collection && collection.params ? getCollectionsContext({ params: collection.params }) : null;
  const isCollections = !!collectionsContext;

  const { isRestoringSavedView, restoreLoading, isLiveView } = useSavedView({
    contextLoading: state.contextLoading,
    page,
  });

  useEffect(() => {
    if (page.name !== 'analyse-products' || !collection?.viewKey || collection?.insightData?.pageSettings) {
      return;
    }
    //get the correct analyse-products page settings for the view
    getPageView(collection?.viewKey).then(res => {
      setInsightPageSettings(res?.data?.['pageSettings'] || {});
    });
  }, [collection?.insightData?.pageSettings, collection?.viewKey, page.name]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const loadingSubscriber = (flag: boolean) => {
      dispatch({
        type: 'SET_CONTEXT_LOADING',
        payload: flag,
      });
    };
    const settingsSubscriber = (context: Context) => {
      dispatch({
        type: 'SET_CONTEXT_STATE',
        payload: context,
      });
    };
    const errorSubscriber = (message: string) => {
      dispatch({
        type: 'SET_ERROR',
        payload: message,
      });
    };
    const { name, url, excludeFilters, excludePriceFilter } = context;
    ContextManager.getInstance().subscribeToLoading(loadingSubscriber);
    ContextManager.getInstance().subscribeToError(errorSubscriber);
    ContextManager.getInstance().registerContext(
      name,
      url,
      excludeFilters,
      excludePriceFilter,
      collectionsContext,
      collection?.isLive || isLiveView
    );

    ContextManager.getInstance().subscribeToSettingsChanges(settingsSubscriber);
    return () => {
      ContextManager.getInstance().unsubscribeFromError(errorSubscriber);
      ContextManager.getInstance().unsubscribeFromLoading(loadingSubscriber);
      ContextManager.getInstance().unsubscribeFromSettingsChanges(settingsSubscriber);
    };
  }, [context.name, context.url]);

  useEffect(() => {
    if (!page) {
      return;
    }
    dispatch({
      type: 'SET_PAGE',
      payload: page,
    });

    if (pageNameRef.current !== page.name) {
      pageNameRef.current = page.name;
      PageSettingsManager.getInstance().registerPage(page.name, page.syncSettings);
    }
  }, [page]);

  useEffect(() => {
    if (!data) {
      return;
    }
    dispatch({
      type: 'SET_DATA',
      payload: data,
    });
  }, [data]);

  const fetchPageDsl = async () => {
    if (pagePending.current || !state.pendingContextState || restoreLoading) {
      return;
    }

    try {
      pagePending.current = true;
      dispatch({
        type: 'START_DSL_FETCH',
        payload: undefined,
      });

      dispatch({
        type: 'SET_IS_DSL_LOADED_FOR_COLLECTION',
        payload: false,
      });

      const {
        page: { url },
        pendingContextState: { settings: pendingSettings, filters: pendingFilters },
      } = state;

      let settings = pendingSettings;
      let filters = pendingFilters;
      if (collectionsContext) {
        settings = collectionsContext.settings;
        filters = collectionsContext.filters;
      }
      const pageParams =
        collection?.insightData?.pageSettings ?? insightPageSettings ?? PageSettingsManager.getInstance().get();

      const dslParams = {
        contextParams: {
          ...settings,
          isLive: getIsLiveValueForProvider(collection.isLive),
          isCurrencyChanged: checkIfCurrencyChanged(ContextManager.getInstance().get(), state)
            ? [{ value: 'true', label: 'CurrencyChanged' }]
            : [],
          isMobile: [{ value: String(isMobile), label: 'MobileFeatureFlag' }],
        },
        filterParams: filters,
        pageParams: pageParams,
      };

      const oldDsl = state.dsl;
      const apiUrl = url + (isBulkExport ? BULK_EXPORT_URL : '');

      const dsl = await getPageDsl(apiUrl, dslParams);

      if (!isMounted()) {
        return;
      }
      dispatch({
        type: 'SET_DSL',
        payload: dsl,
      });

      if (dsl.settings) {
        PageSettingsManager.getInstance().updatePageSettings(dsl.settings.values);
        NavRoutesRegister.getInstance().invalidate(); //to refresh side nav in client-app
      }

      if (collection?.insightData) handleInsightDataChange({ dsl, oldDsl, collection, dispatch });

      pagePending.current = false;
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: String(err.message || DEFAULT_ERROR_MESSAGE),
      });
    } finally {
      pagePending.current = false;
    }
  };

  useEffect(() => {
    if (
      state.page &&
      state.data &&
      state.pageExpired &&
      state.pendingContextState &&
      !state.contextLoading &&
      !state.dslLoading &&
      !restoreLoading &&
      !state.error &&
      !isRestoringSavedView
    ) {
      fetchPageDsl();
      /* istanbul ignore next */
      if (Object.keys(componentHasDataMap).length) {
        const newComponentHasDataMap = Object.keys(componentHasDataMap).reduce((acc, key) => {
          acc[key] = 'isLoading';
          return acc;
        }, {});
        setComponentHasDataMap(newComponentHasDataMap);
      }
    }
  }, [isRestoringSavedView, state, isLiveView, restoreLoading]);

  const updatePageSettings = (updates: Params) => {
    PageSettingsManager.getInstance().updatePageSettings(updates, dispatchCallback);
  };

  const openModal = async (name: string, endpoint: string, params: Params = {}) => {
    const {
      page: { url },
      contextState: { settings, filters },
    } = state;
    try {
      const modalDsl = await pageClient.getModalDsl(makeUrl(url, endpoint), {
        contextParams: settings,
        filterParams: filters,
        pageParams: PageSettingsManager.getInstance().get(),
        modalParams: {
          ...{},
          ...params,
          modal: [name],
        },
      });
      dispatch({
        type: 'SET_MODAL_DSL',
        payload: modalDsl,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const closeModal = () => {
    dispatch({
      type: 'SET_MODAL_DSL',
      payload: undefined,
    });
  };

  const setGlobalWarnings = (globalWarnings: GenericWarning[]) => {
    dispatch({
      type: 'SET_GLOBAL_WARNINGS',
      payload: globalWarnings,
    });
  };

  const setPageWarnings = (pageWarnings: GenericWarning[]) => {
    dispatch({
      type: 'SET_PAGE_WARNINGS',
      payload: pageWarnings,
    });
  };

  const resetGlobalWarnings = () => {
    dispatch({
      type: 'RESET_GLOBAL_WARNINGS',
      payload: true,
    });
  };

  const resetPageWarnings = () => {
    dispatch({
      type: 'RESET_PAGE_WARNINGS',
      payload: true,
    });
  };

  const setCustomizationTray = (trayType: TrayType) => {
    dispatch({
      type: 'SET_CUSTOMIZATION_TRAY_TYPE',
      payload: trayType,
    });
  };

  const setCustomizationTrayTabId = (tabId: FilterTrayTabId) => {
    dispatch({
      type: 'SET_CUSTOMIZATION_TRAY_TAB_ID',
      payload: tabId,
    });
  };

  const setIsFilterTray = (isFilterTray: boolean) => {
    dispatch({
      type: 'SET_IS_FILTER_TRAY',
      payload: isFilterTray,
    });
  };

  const setIsDslLoadedForCollection = (status: boolean) => {
    dispatch({
      type: 'SET_IS_DSL_LOADED_FOR_COLLECTION',
      payload: status,
    });
  };

  /**
   * Sets the data loading state for a given request id.
   * If the data loading state is set to true, the global data loading state is set to true.
   * @param dataLoading - The data loading state.
   * @param requestId - The request id.
   * @returns void
   * */
  const setDataLoading = (dataLoading: boolean, requestId: string) => {
    dataLoadingState.current = {
      ...dataLoadingState.current,
      [requestId]: dataLoading,
    };
    const isGlobalDataLoading = Object.values(dataLoadingState.current).some(Boolean);
    dispatch({
      type: 'SET_DATA_LOADING',
      payload: isGlobalDataLoading,
    });

    if (!isGlobalDataLoading) {
      dataLoadingState.current = {};
    }
  };

  let contextParams: Params | undefined;
  if (state.contextState) {
    const { filters, settings } = state.contextState;
    contextParams = {};
    Object.keys(settings).forEach(key => {
      const setting = settings[key];
      contextParams[key] = setting.map(e => e.value);
    });
    Object.keys(filters).forEach(key => {
      const filter = filters[key];
      contextParams[key] = filter.map(e => e.value);
    });
  }

  let pageParams: Params | undefined;
  if (!state.dslLoading && state.dsl?.settings) {
    const {
      settings: { values, dataSettings },
    } = state.dsl;
    pageParams = {};
    dataSettings.forEach(key => {
      pageParams[key] = values[key];
    });
  }
  const allColumnComponents = useMemo(() => {
    return state.dsl?.sections
      .filter(section => section.type === 'column')
      .flatMap(section => {
        const columnSection = section as ColumnSection;
        return columnSection.columns;
      })
      .filter(column => column.component.noDataInfoType !== 'ignore' && column.component.name !== 'alert');
  }, [state.dsl]);

  const allComponentsHaveNoData = useMemo(() => {
    return (
      Object.keys(componentHasDataMap).every(
        key => componentHasDataMap[key] === false || componentHasDataMap[key] === 'isWarning'
      ) && Object.keys(componentHasDataMap).length === allColumnComponents?.length
    );
  }, [componentHasDataMap]);
  const someComponentsHaveData = useMemo(() => {
    return (
      Object.keys(componentHasDataMap).some(key => componentHasDataMap[key] === true) &&
      Object.keys(componentHasDataMap).length > 0
    );
  }, [componentHasDataMap]);
  const removeInvisibleComponentsFromDataMap = () => {
    // Extract names of visible components from allColumnSections
    const visibleComponentNames = allColumnComponents.map(column => column.component.name);

    // Create a new data map with entries only for visible components
    const filteredDataMap = Object.keys(componentHasDataMap).reduce((acc, componentName) => {
      if (visibleComponentNames.includes(componentName)) {
        acc[componentName] = componentHasDataMap[componentName];
      }
      return acc;
    }, {});

    // Update the state with the modified data map
    setComponentHasDataMap(filteredDataMap);
  };
  return (
    <stateContext.Provider
      value={{
        context,
        data,
        collection,
        page,
        url: state.page?.url,
        pageStamp: state.pageStamp,
        dsl: state.dsl,
        error: state.error,
        dataLoading: state.dataLoading,
        componentHasDataMap,
        someComponentsHaveData,
        allComponentsHaveNoData,
        setComponentHasData: (componentName: string, value: ComponentHasData) => {
          removeInvisibleComponentsFromDataMap();
          setComponentHasDataMap(prevState => {
            return {
              ...prevState,
              [componentName]: value,
            };
          });
        },
        endpoints:
          state.page && state.data
            ? {
                page: state.page.url,
                data: state.data.url,
              }
            : undefined,
        contextParams,
        contextLoading: state.contextLoading,
        pageParams,
        dslLoading: state.dslLoading,
        globalWarnings: state.globalWarnings,
        pageWarnings: state.pageWarnings,
        modalDsl: state.modalDsl,
        updatePageSettings,
        openModal,
        closeModal,
        setGlobalWarnings,
        setPageWarnings,
        resetGlobalWarnings,
        resetPageWarnings,
        setDataLoading,
        trayType: state.trayType,
        setCustomizationTray,
        customizationTrayTabId: state.customizationTrayTabId,
        setCustomizationTrayTabId,
        isCollections,
        isFilterTray: state.isFilterTray,
        setIsFilterTray,
        isDslLoadedInCollection: state.isDslLoadedInCollection,
        setIsDslLoadedForCollection,
        upsellContext,
      }}
    >
      {children}
    </stateContext.Provider>
  );
}

function useCorePageState() {
  const context = useContext(stateContext);
  if (!context) {
    throw new Error('useCorePageState must be used within a provider');
  }
  return context;
}

export { CorePageProvider, useCorePageState };
